'use client';
import { useEffect, useState } from 'react';

import { usePathname } from 'next/navigation';
import Image from 'next/image';

import { useToggle } from '@hooks/common';
import { useChatGPTContext } from '@contexts/chatGPT';
import RobotImage from '@assets/images/robot.png';
import { useAuthState } from '@contexts/auth/AuthContext';
import { logChatgptEvent } from '@utils/gtm/chatgpt-gtm';
import { getSafeLocalStorageItem } from '@utils/localStorage/getSafeLocalStorage';

import { ChatGPTButton } from './styled';
import ChatGPTTooltip from './ChatGPTTooltip';
import { DEFAULT_CHATGPT_MESSAGE } from './utils';
import { useHandleOnRedirection } from './hooks';
import { ChatGPTKVKK } from './ChatGPTKVKK';
import ChatGPTModal from './ChatGPTModal';

interface Props {
  pageType?: string;
  homePageResponsive?: boolean;
}

const ChatGPTLoader = ({ pageType, homePageResponsive }: Props) => {
  const path = usePathname();
  const [isChatGPTModalOpen, toggleChatGPTModal] = useToggle(false);
  const [control, setControl] = useState(false);
  const [isKVKKModalOpen, toggleKVKKModal] = useToggle(false);
  const { loginInfo } = useAuthState();
  const isLoggedIn = !!loginInfo?.token;

  const {
    chatGPTMessage,
    setChatGPTMessage,
    visibleChatGPT,
    visibleChatGPTTooltip,
    setVisibleChatGPTTooltip,
    chatbotStatus,
  } = useChatGPTContext();

  useHandleOnRedirection(() => {
    setVisibleChatGPTTooltip(false);
  });

  useEffect(() => {
    if (
      pageType === 'hotelList' ||
      pageType === 'hotelDetail' ||
      pageType === 'tourList' ||
      pageType === 'tourDetail' ||
      path?.includes('/ic-hat-ucuslari') ||
      path?.includes('/dis-hat-ucuslari')
    )
      return;
    setChatGPTMessage(DEFAULT_CHATGPT_MESSAGE);
  }, [pageType, setChatGPTMessage, path]);

  // =====================================
  // Disable body scroll when the gallery open
  // =====================================
  useEffect(() => {
    const body = document.querySelector('body');

    if (body && (isChatGPTModalOpen || isKVKKModalOpen)) {
      body.style.overflow = 'hidden';
    }

    return () => {
      if (body) {
        body.style.overflow = 'initial';
      }
    };
  }, [isChatGPTModalOpen, isKVKKModalOpen]);

  // ===================================
  // Toggle the chatGPT tooltip
  // ==================================
  useEffect(() => {
    if (!visibleChatGPT) return;
    if (!chatbotStatus) return;

    if (sessionStorage.getItem('isTooltipViewed') === 'true') return;

    setVisibleChatGPTTooltip(true);
  }, [path, visibleChatGPT, setVisibleChatGPTTooltip, chatbotStatus]);

  // =====================================
  // Handle Click on the chatGPT button
  // =====================================
  const handleChatGPTButtonClick = () => {
    logChatgptEvent('opened');
    setControl(true);
    let storedKVKKValue = '';

    if (isLoggedIn) {
      storedKVKKValue = getSafeLocalStorageItem('CHAPT_GPT_KVKK_AGREED') ?? '';
    } else {
      storedKVKKValue = sessionStorage.getItem('CHAPT_GPT_KVKK_AGREED') ?? '';
    }
    if (storedKVKKValue === 'true') {
      toggleChatGPTModal();
    } else {
      toggleKVKKModal();
    }
  };
  useEffect(() => {
    if (control) toggleChatGPTModal();
  }, [path]);

  return chatbotStatus && visibleChatGPT ? (
    <>
      {isChatGPTModalOpen && control ? (
        <ChatGPTModal
          isChatGPTModalOpen={isChatGPTModalOpen}
          toggleChatGPTModal={toggleChatGPTModal}
          setControl={setControl}
        />
      ) : null}
      {isKVKKModalOpen ? (
        <ChatGPTKVKK
          toggleVisible={toggleKVKKModal}
          onKVKKAgree={toggleChatGPTModal}
        />
      ) : null}
      <ChatGPTTooltip
        homePageResponsive={homePageResponsive}
        message={chatGPTMessage ?? ''}
        open={!!visibleChatGPTTooltip}
        toggleTooltip={setVisibleChatGPTTooltip}
      >
        <ChatGPTButton onClick={handleChatGPTButtonClick}>
          <Image src={RobotImage} width={63} height={60} alt='chatgpt robot ' />
        </ChatGPTButton>
      </ChatGPTTooltip>
    </>
  ) : null;
};

export default ChatGPTLoader;
